import { useCart } from "../../context/CartContext";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

export default function CartPage() {
  const { cartItems, updateQty, removeFromCart, clearCart, total } = useCart();


  const handleQuantityChange = (item, newQty) => {
    const qty = parseInt(newQty, 10);
    if (isNaN(qty) || qty < 1) {
      toast.warning("Quantity must be at least 1");
      return;
    }
    updateQty(item.key, qty);
  };

  const handleRemove = (item) => {
    removeFromCart(item.key);
    toast.info("Removed from cart");
  };

  const handleClearCart = () => {
    if (window.confirm("Clear entire cart?")) {
      clearCart();
      toast.success("Cart cleared");
    }
  };

  return (
    <div className="max-w-4xl font-libreFranklin mx-auto py-8 sm:py-12 px-4 mt-16 sm:mt-24">
      <h2 className="text-2xl sm:text-3xl font-bold mb-6" style={{ fontFamily: "'Sustainability', sans-serif" }}>
        Your Cart
      </h2>

      {cartItems.length === 0 ? (
        <div className="text-center text-gray-500 py-12">
          <svg
            className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <p className="text-lg sm:text-xl mb-4">Your cart is empty.</p>
          <Link
            to="/category/women"
            className="inline-block bg-black text-white px-6 py-3 rounded hover:bg-gray-800 transition-colors"
          >
            Continue Shopping
          </Link>
        </div>
      ) : (
        <>
          {/* Cart Items */}
          <div className="space-y-4 mb-6 sm:mb-8">
            {cartItems.map((item) => (
              <div
                key={item.key}
                className="border rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow bg-white"
              >
                {/* Mobile Layout */}
                <div className="flex gap-3 sm:gap-4">
                  {/* Image */}
                  <div className="flex-shrink-0">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded"
                    />
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-base sm:text-lg mb-1 truncate">
                      {item.title}
                    </h4>

                    {/* Attributes - Compact on mobile */}
                    <div className="text-xs sm:text-sm text-gray-600 space-y-0.5 mb-2">
                      {item.color && (
                        <p>Color: <span className="text-gray-800">{item.color}</span></p>
                      )}
                      {item.size && (
                        <p>Size: <span className="text-gray-800">{item.size}</span></p>
                      )}
                    </div>

                    {/* Price */}
                    <p className="font-semibold text-base sm:text-lg">
                      ${Number(item.price).toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Bottom Row - Quantity, Subtotal, Remove */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  {/* Quantity Controls */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleQuantityChange(item, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                      className="w-8 h-8 flex items-center justify-center border rounded-l bg-gray-50 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleQuantityChange(item, e.target.value)}
                      className="w-12 h-8 px-1 border-t border-b text-center text-sm focus:outline-none"
                    />
                    <button
                      onClick={() => handleQuantityChange(item, item.quantity + 1)}
                      className="w-8 h-8 flex items-center justify-center border rounded-r bg-gray-50 hover:bg-gray-100"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>

                  {/* Subtotal */}
                  <p className="text-sm font-medium text-gray-700">
                    ${(Number(item.price) * item.quantity).toFixed(2)}
                  </p>

                  {/* Remove Button */}
                  <button
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                    onClick={() => handleRemove(item)}
                    aria-label="Remove item"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Summary - Fixed bottom on mobile */}
          <div className="border-t pt-4 sm:pt-6">
            {/* Total */}
            <div className="flex justify-between items-center mb-4">
              <div>
                <p className="text-sm text-gray-500">
                  {cartItems.length} item{cartItems.length > 1 ? "s" : ""}
                </p>
                <p className="text-2xl sm:text-3xl font-bold">
                  ${total.toFixed(2)}
                </p>
              </div>
              
              {/* Clear Cart - Desktop only */}
              <button
                className="hidden sm:block px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                onClick={handleClearCart}
              >
                Clear Cart
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/checkout"
                className="flex-1 py-3 sm:py-2 bg-black text-white text-center rounded hover:bg-gray-800 transition-colors font-medium"
              >
                Proceed to Checkout
              </Link>
              
              {/* Clear Cart - Mobile */}
              <button
                className="sm:hidden py-3 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                onClick={handleClearCart}
              >
                Clear Cart
              </button>
            </div>

            {/* Continue Shopping */}
            <Link
              to="/category/women"
              className="block mt-4 text-center sm:text-left text-sm text-gray-600 hover:text-black"
            >
              ← Continue Shopping
            </Link>
          </div>
        </>
      )}
    </div>
  );
}